import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Table,
  Group,
  LoadingOverlay,
  Alert,
  ActionIcon,
  Badge,
  Button,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconEdit,
  IconTrash,
  IconPlus,
} from "@tabler/icons-react";
import { notifySuccess } from "../../utils/notify";
import { StandardTableShell } from "../common/StandardTable";
import { SettingsPageLayout, SettingsPageHeader } from "./common";
import { useSalon } from "../../context/SalonContext";
import { useApp } from "../../context/AppContext";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import PolicyFormModal from "./PolicyFormModal";
import { Policy, POLICY_CATEGORY_LABELS } from "../../types/policies";

const categoryColor = (category: Policy["category"]): string => {
  switch (category) {
    case "cancellation":
      return "var(--brand-teal)";
    case "rescheduling":
      return "var(--category-alt)";
    case "no_show":
      return "var(--brand-burnt-orange)";
    case "late":
      return "var(--brand-lime-green)";
    default:
      return "gray";
  }
};

// Plain-text preview of a rich-text (HTML) body for the list view.
const bodyPreview = (html: string): string => {
  if (!html) return "";
  try {
    const text =
      new DOMParser().parseFromString(html, "text/html").body.textContent || "";
    return text.trim();
  } catch {
    return html.replace(/<[^>]*>/g, " ").trim();
  }
};

const PoliciesSettings: React.FC = () => {
  const { businessInfo, currentUser } = useSalon() as {
    businessInfo: any;
    currentUser: any;
  };
  const { getSalonPolicies, deletePolicy } = useApp();

  const canEdit =
    currentUser?.role === "Owner" || currentUser?.role === "Manager";

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState<Policy | null>(null);

  const [deletingPolicyId, setDeletingPolicyId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchPolicies = useCallback(async () => {
    if (!businessInfo?.id) return;
    setIsLoading(true);
    setLoadError(null);
    try {
      const response = await getSalonPolicies(businessInfo.id);
      setPolicies(Array.isArray(response) ? response : []);
    } catch (error: any) {
      console.error("Error fetching policies:", error);
      setLoadError(
        error?.message || "Failed to load policies. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [businessInfo?.id, getSalonPolicies]);

  useEffect(() => {
    if (businessInfo?.id) {
      fetchPolicies();
    }
  }, [businessInfo?.id, fetchPolicies]);

  const handleAddClick = () => {
    setEditingPolicy(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (policy: Policy) => {
    setEditingPolicy(policy);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingPolicy(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPolicyId) return;
    const deleted = policies.find((p) => p.id === deletingPolicyId);
    setIsDeleting(true);
    try {
      await deletePolicy(deletingPolicyId);
      // Update the table in place rather than refetching, so the row drops out
      // smoothly without flashing the full-page loading state.
      setPolicies((prev) => prev.filter((p) => p.id !== deletingPolicyId));
      setDeletingPolicyId(null);
      notifySuccess(`Successfully deleted ${deleted?.title ?? "policy"}`, {
        title: "Success",
      });
    } catch (error: any) {
      console.error("Error deleting policy:", error);
      setLoadError(
        error?.message || "Failed to delete policy. Please try again.",
      );
      setDeletingPolicyId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <SettingsPageLayout
      header={
        <SettingsPageHeader
          title="Policies"
          subtitle="Create the policies clients agree to when booking. They appear at booking and in the reschedule/cancel flow."
          actions={
            canEdit ? (
              <Button
                color="var(--brand-teal)"
                leftSection={<IconPlus size={16} />}
                onClick={handleAddClick}
              >
                Add Policy
              </Button>
            ) : undefined
          }
        />
      }
    >
      {loadError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
          withCloseButton
          onClose={() => setLoadError(null)}
        >
          {loadError}
        </Alert>
      )}

      <Box pos="relative" mih={isLoading ? 200 : undefined}>
        <LoadingOverlay visible={isLoading} />
        <StandardTableShell minWidth={600} ariaLabel="Salon policies">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={140}>Category</Table.Th>
              <Table.Th>Title</Table.Th>
              <Table.Th>Preview</Table.Th>
              {canEdit && <Table.Th w={90}>Actions</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {policies.map((policy) => (
              <Table.Tr key={policy.id}>
                <Table.Td>
                  <Badge
                    size="sm"
                    variant="light"
                    color={categoryColor(policy.category)}
                  >
                    {POLICY_CATEGORY_LABELS[policy.category] ?? policy.category}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={500}>
                    {policy.title}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {bodyPreview(policy.body)}
                  </Text>
                </Table.Td>
                {canEdit && (
                  <Table.Td>
                    <Group gap={4} wrap="nowrap">
                      <ActionIcon
                        variant="subtle"
                        color="teal"
                        size="sm"
                        aria-label="Edit policy"
                        onClick={() => handleEditClick(policy)}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="subtle"
                        color="red"
                        size="sm"
                        aria-label="Delete policy"
                        onClick={() => setDeletingPolicyId(policy.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
            {policies.length === 0 && !isLoading && (
              <Table.Tr>
                <Table.Td colSpan={canEdit ? 4 : 3}>
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    No policies yet.
                    {canEdit ? ' Click "Add Policy" to create one.' : ""}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </StandardTableShell>
      </Box>

      {canEdit && businessInfo?.id && (
        <>
          <PolicyFormModal
            opened={isModalOpen}
            onClose={handleModalClose}
            salonId={businessInfo.id}
            editingPolicy={editingPolicy}
            onSaved={fetchPolicies}
          />
          <DeleteConfirmationModal
            isOpen={deletingPolicyId !== null}
            onClose={() => setDeletingPolicyId(null)}
            onConfirm={handleDeleteConfirm}
            title="Delete Policy"
            message="Are you sure you want to delete this policy? This action cannot be undone."
            isLoading={isDeleting}
          />
        </>
      )}
    </SettingsPageLayout>
  );
};

export default PoliciesSettings;
